import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  WELCOME_MESSAGE,
  getAssistantResponse,
  assistantReplyDelay,
} from '../lib/chatAssistant'
import './TerminalWarning.css'

const TerminalWarning = ({ isOpen, onToggle }) => {
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const messagesEndRef = useRef(null)
  const replyTimerRef = useRef(null)

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{ ...WELCOME_MESSAGE, time: new Date().toLocaleTimeString() }])
    }
  }, [isOpen, messages.length])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isTyping])

  useEffect(() => () => {
    if (replyTimerRef.current) clearTimeout(replyTimerRef.current)
  }, [])

  const sendMessage = (userMessage) => {
    if (!userMessage.trim() || isTyping) return

    const currentTime = new Date().toLocaleTimeString()

    setMessages((prev) => [
      ...prev,
      { type: 'user', content: userMessage, time: currentTime },
    ])
    setInput('')
    setIsTyping(true)

    if (replyTimerRef.current) clearTimeout(replyTimerRef.current)

    replyTimerRef.current = setTimeout(() => {
      const { content, actions } = getAssistantResponse(userMessage)

      setMessages((prev) => [
        ...prev,
        {
          type: 'assistant',
          content,
          actions,
          time: new Date().toLocaleTimeString(),
        },
      ])
      setIsTyping(false)
    }, assistantReplyDelay(userMessage))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (input.trim() && !isTyping) {
      sendMessage(input)
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  const handleNavigationClick = (section) => {
    onToggle()
    const element = document.getElementById(section)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const handleQuickAction = (action) => {
    if (action.section) {
      handleNavigationClick(action.section)
      return
    }
    if (action.message) {
      sendMessage(action.message)
    }
  }

  const extractNavActions = (content) => {
    const linkRegex = /\[([^\]]+)\]\(#([^)]+)\)/g
    const actions = []
    const seen = new Set()
    let match = linkRegex.exec(content)

    while (match) {
      const label = match[1]
      const section = match[2]
      const key = `${label}-${section}`
      if (!seen.has(key)) {
        seen.add(key)
        actions.push({ id: key, label, section })
      }
      match = linkRegex.exec(content)
    }

    return actions
  }

  const renderBoldText = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g)
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={index}>{part.slice(2, -2)}</strong>
      }
      return part
    })
  }

  const renderMessageContent = (content) => {
    const displayContent = content.replace(/\[([^\]]+)\]\(#([^)]+)\)/g, '$1')
    return <>{renderBoldText(displayContent)}</>
  }

  const renderMessageActions = (message) => {
    const actions = message.actions?.length
      ? message.actions
      : extractNavActions(message.content)

    if (!actions.length || message.type !== 'assistant') return null

    return (
      <div className="chat-widget-actions">
        {actions.map((action) => (
          <button
            key={action.id}
            type="button"
            className="chat-widget-action-btn"
            onClick={() => handleQuickAction(action)}
            disabled={isTyping}
          >
            {action.label}
          </button>
        ))}
      </div>
    )
  }

  return (
    <div className="chat-widget">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="chat-widget-panel"
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="chat-widget-header">
              <div className="chat-widget-header-info">
                <div className="chat-widget-avatar" aria-hidden="true">
                  <img src="/images/picture.png" alt="" className="chat-widget-avatar-img" />
                </div>
                <div className="chat-widget-title-wrap">
                  <h3 className="chat-widget-title">Site Assistant</h3>
                  <div className="chat-widget-status">
                    <span className="chat-widget-status-dot" />
                    Local · Instant replies
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="chat-widget-close"
                onClick={onToggle}
                aria-label="Close chat"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <div className="chat-widget-messages">
              {messages.map((message, index) => (
                <motion.div
                  key={index}
                  className={`chat-widget-message chat-widget-message--${message.type}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25 }}
                >
                  <div className={`chat-widget-bubble chat-widget-bubble--${message.type}`}>
                    {renderMessageContent(message.content)}
                    {renderMessageActions(message)}
                  </div>
                </motion.div>
              ))}

              {isTyping && (
                <div className="chat-widget-message chat-widget-message--assistant">
                  <div className="chat-widget-bubble chat-widget-bubble--typing">
                    <span className="chat-widget-typing">Typing...</span>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            <form className="chat-widget-form" onSubmit={handleSubmit}>
              <div className="chat-widget-input-row">
                <textarea
                  className="chat-widget-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask about this site..."
                  disabled={isTyping}
                  rows={1}
                />
                <button
                  type="submit"
                  className="chat-widget-send"
                  disabled={!input.trim() || isTyping}
                >
                  Send
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        type="button"
        className={`chat-widget-launcher${isOpen ? ' chat-widget-launcher--open' : ''}`}
        onClick={onToggle}
        aria-label={isOpen ? 'Close site assistant' : 'Open site assistant'}
      >
        {isOpen ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
          </svg>
        ) : (
          <img src="/images/picture.png" alt="" className="chat-widget-launcher-logo" />
        )}
      </button>
    </div>
  )
}

export default TerminalWarning
